import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { FormProvider, useForm } from 'react-hook-form';
import FormTextField from 'components/IAP/formFields/FormTextField';
import FormSelectField from 'components/IAP/formFields/FormSelectField';

import { AssignmentListRow } from './AssignmentContainer';
import { calculateGridTemplateColumns } from 'components/DesignSystems/FilterHeader/calculateGridTemplateColumns';
import FilterCardHeader from 'components/DesignSystems/FilterHeader/FilterCardHeader';

import {
  PlannedResource,
  useCreateWorkAssignmentListAttachment,
  useUpdateWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import { SectionChiefInfo } from './useSectionChiefResolver';
import { ProposedStatusOption, Status } from './AssignmentList';
import AssignmentCardResourceSectionEdit from './AssignmentCardResourceSectionEdit';
import usePlannedResourceResolver from './usePlannedResourceResolver';
import FormTextAreaField from 'components/IAP/formFields/FormTextAreaField';
import { NULL_VALUE } from 'constants/displayConstants';

type AssignmentCardEditProps = {
  row: AssignmentListRow;
  setIsEditing: (isEditing: boolean) => void;
  columnNumber: number;
  sectionChief?: SectionChiefInfo;
  periodNumber: number;
};

export type AssignmentCardFormData = {
  walaId: string | null;
  sectionChief: string;
  workGroupId: string;
  branch: string;
  division: string;
  walaGroup: string;
  stagingArea: string;
  proposedStatus: Status;
  branchDirector?: string;
  divisionGroupSupervisor?: string;
  resource?: Record<
    string,
    {
      resourceName: string;
      leader: string;
      numberOfPersons: number;
      contactInfo: string;
      reportingInfo: string;
      has204a: boolean;
      resourceBranch?: string;
      resourceDivision?: string;
      resourceGroup?: string;
      resourceStrikeTeam?: string;
      resourceTaskForce?: string;
      resourceAssignmentLocation?: string;
      resourceSpecialInstructions?: string;
      resourceSafetyPlanLocation?: string;
    }
  >;
  medicalCommunication?: string;
  evacuationCommunication?: string;
  otherCommunication?: string;
  specialInstructions?: string;
};

const AssignmentCardEdit = ({
  row,
  setIsEditing,
  columnNumber,
  sectionChief,
  periodNumber,
}: AssignmentCardEditProps) => {
  const mapProposedStatus = (status: ProposedStatusOption): Status => {
    switch (status) {
      case 'Draft':
        return 'draft';
      case 'Assigned':
        return 'in_progress';
      case 'Completed':
      case 'Submitted Complete for Review':
        return 'completed';
      case 'Reported Incomplete for Review':
      case 'Incomplete':
        return 'incomplete';
      default:
        return 'draft'; // Default to draft if unknown
    }
  };

  // Todo (aeortic): This memo is shared between this and the view mode component.
  // We should consider creating a hook for this.
  const plannedResourceData = usePlannedResourceResolver(
    row.plannedResources,
    row.walaId
  );

  const formMethods = useForm<AssignmentCardFormData>({
    mode: 'onChange',
    defaultValues: {
      walaId: row.walaId,
      sectionChief: sectionChief?.personName ?? NULL_VALUE,
      workGroupId: row.workGroupId,
      branch: row.branch,
      division: row.division,
      walaGroup: row.walaGroup,
      stagingArea: row.stagingArea,
      proposedStatus: mapProposedStatus(row.proposedStatus),
      resource: plannedResourceData.reduce(
        (acc, res) => {
          acc[res.id] = {
            resourceName: res.name,
            leader: res.leader,
            numberOfPersons: res.numberOfPersons,
            contactInfo: res.contactInfo,
            reportingInfo: res.reportingInfo,
            has204a: res.has204a,
            resourceBranch: res.resourceBranch,
            resourceDivision: res.resourceDivision,
            resourceGroup: res.resourceGroup,
            resourceStrikeTeam: res.resourceStrikeTeam,
            resourceTaskForce: res.resourceTaskForce,
            resourceAssignmentLocation: res.resourceAssignmentLocation,
            resourceSpecialInstructions: res.resourceSpecialInstructions,
            resourceSafetyPlanLocation: res.resourceSafetyPlanLocation,
          };
          return acc;
        },
        {} as NonNullable<AssignmentCardFormData['resource']>
      ),
      specialInstructions: row.specialInstructions,
      medicalCommunication: row.medicalCommunication,
      evacuationCommunication: row.evacuationCommunication,
      otherCommunication: row.otherCommunication,
    },
  });

  const { handleSubmit } = formMethods;

  const { mutate: create } = useCreateWorkAssignmentListAttachment();
  const { mutate: update } = useUpdateWorkAssignmentListAttachment();

  const onSubmit = (data: AssignmentCardFormData) => {
    const editedResources = Object.entries(data.resource ?? {}).reduce(
      (acc, [id, res]) => {
        acc.push({
          id,
          leader: res.leader,
          numberOfPersons: Number(res.numberOfPersons),
          contactInfo: res.contactInfo,
          reportingInfo: res.reportingInfo,
          has204a: !!res.has204a,
          resourceBranch: res.resourceBranch,
          resourceDivision: res.resourceDivision,
          resourceGroup: res.resourceGroup,
          resourceStrikeTeam: res.resourceStrikeTeam,
          resourceTaskForce: res.resourceTaskForce,
          resourceAssignmentLocation: res.resourceAssignmentLocation,
          resourceSpecialInstructions: res.resourceSpecialInstructions,
          resourceSafetyPlanLocation: res.resourceSafetyPlanLocation,
        });
        return acc;
      },
      [] as PlannedResource[]
    );

    const payload = {
      workGroupId: data.workGroupId,
      branch: data.branch,
      division: data.division,
      walaGroup: data.walaGroup,
      stagingArea: data.stagingArea,
      proposedStatus: data.proposedStatus,
      branchDirector: data.branchDirector,
      divisionGroupSupervisor: data.divisionGroupSupervisor,
      workAssignmentIds: row.workAssignments.map((wa) => wa.id),
      plannedResources: editedResources,
      medicalCommunication: data.medicalCommunication,
      evacuationCommunication: data.evacuationCommunication,
      otherCommunication: data.otherCommunication,
      specialInstructions: data.specialInstructions,
    };

    if (data.walaId) {
      update({ walaId: data.walaId, ...payload });
    } else {
      create({ periodNumber, ...payload });
    }

    setIsEditing(false);
  };

  const gridTemplateColumns = calculateGridTemplateColumns(columnNumber);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FilterCardHeader gridTemplateColumns={gridTemplateColumns}>
          <div onClick={() => setIsEditing(false)} role="button">
            <SharedIcon iconName="keyboard_arrow_down" size="sm" />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormTextField
              title="WorkGroup"
              fieldName="workGroupName"
              disabled
            />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormTextField title="Branch" fieldName="branch" />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormTextField title="Division" fieldName="division" />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormTextField title="Group" fieldName="walaGroup" />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormTextField title="Staging Area" fieldName="stagingArea" />
          </div>
          <div style={{ paddingRight: '10px' }}>
            <FormSelectField
              title="Status"
              fieldName="proposedStatus"
              options={[
                { value: 'draft', label: 'Draft' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'completed', label: 'Completed' },
                { value: 'incomplete', label: 'Incomplete' },
              ]}
              disabled
            />
          </div>
          <div>
            <StylishNewButton
              className="button--icon me-2"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <SharedIcon iconName="map" />
            </StylishNewButton>
            <StylishNewButton
              className="button--icon"
              onClick={(e) => {
                setIsEditing(false);
                e.stopPropagation();
              }}
            >
              <SharedIcon iconName="cancel" />
            </StylishNewButton>
          </div>
        </FilterCardHeader>
        <div className="filter-card-content">
          <div className="filter-card-content-grid p-4">
            <FormTextField
              title={`${sectionChief?.positionName}`}
              fieldName="sectionChief"
              disabled
            />
            <FormTextField title="Branch Director" fieldName="branchDirector" />
            <FormTextField
              title="Division/Group Supervisor"
              fieldName="divisionGroupSupervisor"
            />
          </div>
        </div>
        {plannedResourceData.length !== 0 && (
          <div className="m-3">
            <AssignmentCardResourceSectionEdit
              plannedResources={plannedResourceData}
            />
          </div>
        )}
        <div className="filter-card-content">
          <div className="fw-bold">
            <span>Special Instructions:</span>
          </div>
          <div className="px-4">
            <FormTextAreaField
              title=""
              fieldName="specialInstructions"
              rules={{
                maxLength: {
                  value: 1000,
                  message: 'This maximum length is 1000 characters',
                },
              }}
            />
          </div>
        </div>
        <div className="filter-card-content">
          <div className="fw-bold">
            <span>Emergency Communication:</span>
          </div>
          <div className="filter-card-content-grid p-4">
            <FormTextField
              title="Medical Communication"
              fieldName="medicalCommunication"
            />
            <FormTextField
              title="Evacuation Communication"
              fieldName="evacuationCommunication"
            />
            <FormTextField
              title="Other Communication"
              fieldName="otherCommunication"
            />
          </div>
        </div>
        <div className="m-3 d-flex align-items-center">
          <StylishNewButton type="submit">Save as Draft</StylishNewButton>
          <StylishNewButton
            type="button"
            className="ms-1"
            style={{
              backgroundColor: 'transparent',
              border: '1px solid darkgray',
            }}
            onClick={() => setIsEditing(false)}
          >
            Cancel
          </StylishNewButton>
        </div>
      </form>
    </FormProvider>
  );
};

export default AssignmentCardEdit;
